'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import { useState, useEffect } from 'react';
import Link from '@eliadmin/core/src/components/link';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import type { TDynamicData } from '../../types/dynamic-data';
import { HtmlDescription } from '../shared/html-description';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Pagination } from 'swiper/modules';

export function ProductsByCategory({ products }: { products: TDynamicData[] }) {
  const [activeCategory, setActiveCategory] = useState<TDynamicData>(products[0]);

  useEffect(() => {
    setActiveCategory(products[0]);
  }, [products]);

  const handleSlideChange = (swiper: any) => {
    setActiveCategory(products[swiper.activeIndex]);
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/\//g, '-')
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-+/g, '-')
      .trim();
  };

  return (
    <section className={cn('custom-section', 'my-16 sm:my-20 lg:my-24')}>
      {/* desktop version */}
      <div
        className={cn(
          'custom-container',
          'hidden items-end justify-between gap-16 md:flex lg:gap-24',
        )}
      >
        <div className="flex w-full max-w-[643px] flex-col items-start justify-start gap-8">
          <Image src={'/images/terrain.svg'} alt="Terrain" width={56} height={56} unoptimized />
          <h2
            className={cn(
              'font-heading uppercase',
              'w-full text-3xl font-light text-foreground sm:text-4xl lg:text-5xl',
            )}
          >
            KDE ŽIADNY TERÉN <strong className="font-medium">NIE JE LIMIT</strong>
          </h2>
          <HtmlDescription
            content="<p>Trek je svetovým lídrom v technológii horských bicyklov. Nie je prekvapením, že naše horské bicykle sú technologicky najvyspelejšie na trhu. Tu sa inovácie neobmedzujú len na bicykle najvyššej triedy. Každý model je nabitý funkciami a detailmi, vďaka ktorým bude každá jazda na akomkoľvek traili lepšia.</p>"
            color="foreground"
            className="w-full text-left"
          />
          <div className="flex w-full flex-col items-start justify-start gap-0">
            {products.map(product => (
              <PreviewProductCard
                key={product.name}
                product={product}
                isActive={activeCategory?.id === product.id}
                setActiveCategory={setActiveCategory}
              />
            ))}
          </div>
        </div>
        <div
          className={cn(
            'flex w-full max-w-[785px] flex-col items-start justify-start gap-2',
            'ea-products-by-category__product-container',
          )}
        >
          {activeCategory && (
            <>
              <h3 data-ea-visual-tag="h4"
                className={cn(
                  'font-heading uppercase',
                  'text-5xl font-medium text-[#D0D0D0] lg:text-6xl xl:text-7xl',
                )}
              >
                {activeCategory.name}
              </h3>
              <div className="relative h-[460px] w-full">
                <Image
                  src={activeCategory.image.url}
                  alt={resolveImageAlt(activeCategory?.image, activeCategory?.name)}
                  style={{ objectFit: 'contain', objectPosition: 'center' }}
                  fill
                  sizes="70vw"
                />
              </div>
            </>
          )}
        </div>
      </div>
      {/* mobile version */}
      <div className="flex w-full flex-col items-start justify-start gap-8 md:hidden">
        <Image src={'/images/terrain.svg'} alt="Terrain" width={56} height={56} unoptimized />
        <h2
          className={cn(
            'font-heading uppercase',
            'w-full text-3xl font-light text-foreground sm:text-4xl lg:text-5xl',
          )}
        >
          KDE ŽIADNY TERÉN <strong className="font-medium">NIE JE LIMIT</strong>
        </h2>
        <HtmlDescription
          content="<p>Trek je svetovým lídrom v technológii horských bicyklov. Nie je prekvapením, že naše horské bicykle sú technologicky najvyspelejšie na trhu. Tu sa inovácie neobmedzujú len na bicykle najvyššej triedy. Každý model je nabitý funkciami a detailmi, vďaka ktorým bude každá jazda na akomkoľvek traili lepšia.</p>"
          color="foreground"
          className="w-full text-left"
        />
        <Swiper
          pagination={true}
          onSlideChange={handleSlideChange}
          modules={[Pagination]}
          slidesPerView={1}
          spaceBetween={12}
          className="mySwiper ea-products-by-category__swiper h-fit w-full"
        >
          {products?.map(slide => (
            <SwiperSlide key={slide.name} className="relative w-full">
              <div className="flex w-full flex-col items-center justify-center gap-2">
                <div className="relative h-[206px] w-full sm:h-[300px]">
                  <Image
                    src={slide?.image?.url ?? '/images/placeholder.png'}
                    alt={resolveImageAlt(slide?.image, slide?.name)}
                    fill
                    style={{ objectFit: 'contain', objectPosition: 'center' }}
                    sizes="100vw"
                  />
                </div>
                <h3 data-ea-visual-tag="h4" className="mt-12 text-center font-heading text-4xl font-medium uppercase text-primary sm:text-5xl">
                  {slide.name}
                </h3>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <Button asChild className="mx-auto -mt-4 w-full sm:w-fit">
          <Link prefetch={false} href={activeCategory ? generateSlug(activeCategory.name) : '#'}>
            PREZERAŤ PONUKU
          </Link>
        </Button>
      </div>
    </section>
  );
}

const PreviewProductCard = ({
  product,
  isActive,
  setActiveCategory,
}: {
  product: TDynamicData;
  isActive: boolean;
  setActiveCategory: (product: TDynamicData) => void;
}) => {
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/\//g, '-')
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-+/g, '-')
      .trim();
  };

  return (
    <Link
      prefetch={false}
      href={product.name === 'DOWNHILL/DIRT' ? '/downhilldirt-1' : generateSlug(product.name)}
      className={cn(
        'group',
        'flex w-full items-center justify-between gap-6 p-4',
        isActive && 'text-primary',
      )}
      onMouseEnter={() => setActiveCategory(product)}
      style={{
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      }}
    >
      <h3 data-ea-visual-tag="h4"
        className={cn(
          'whitespace-nowrap font-heading font-medium uppercase',
          'transition-[font-size,color] duration-300 ease-in-out',
          isActive ? 'text-4xl text-primary' : 'text-3xl text-[#969696]',
        )}
        style={{
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden',
          WebkitPerspective: '1000px',
          perspective: '1000px',
        }}
      >
        {product.name}
      </h3>
      <div
        className={cn(
          'h-[1px] w-full bg-primary transition-opacity',
          'group-hover:opacity-100',
          isActive ? 'opacity-100' : 'opacity-0',
        )}
      />
      <Button
        asChild
        className={cn(
          'whitespace-nowrap transition-opacity group-hover:opacity-100',
          isActive ? 'opacity-100' : 'opacity-0',
        )}
      >
        <span>PREZERAŤ PONUKU</span>
      </Button>
    </Link>
  );
};
